import React, { useEffect, useState } from 'react';
import { Input, message, Row, Table } from 'antd';
import { observer } from 'mobx-react';
import UIStore from '@/stores/UIStore';
import dayjs from 'dayjs';
import { Price } from '@partsbadger/library';
import { useQueryParameters } from '@partsbadger/utils';
import QuoteStore from '../stores/QuoteStore';
import SalesOrderStore from '../stores/SalesOrderStore';
import { QuoteProps, SalesOrderProps } from '../types/types';
import { ColumnProps } from 'antd/es/table';
import { useHistory } from 'react-router';
import { EyeInvisibleOutlined } from '@ant-design/icons';

const QuoteResults = (props: { loading: boolean; quotes: QuoteProps[] }) => {
    const history = useHistory();

    const columns_quotes: ColumnProps<QuoteProps>[] = [
        {
            title: 'Number',
            dataIndex: 'name',
        },
        {
            title: 'Date',
            dataIndex: 'created_time',
            render: text => (text ? dayjs(text).format('YYYY-MM-DD') : '---'),
        },
        {
            title: 'Grand Total',
            dataIndex: 'grand_total',
            render: (text, record) => {
                if (!record.completed) {
                    return (
                        <div className="cursor-pointer">
                            <span className="text-xs md:text-sm mr-0 md:mr-2">Quoting</span>
                            <EyeInvisibleOutlined />
                        </div>
                    );
                }
                return <Price value={record.grand_total} />;
            },
        },
    ];

    return (
        <Table<QuoteProps>
            bordered
            rowKey={'id'}
            onRow={record => {
                return {
                    onClick: () => {
                        if (record.completed) {
                            history.push('/my-account/quotes/' + record.id);
                        } else {
                            message.info(
                                'This quote is not completed yet, after completion you will be able to access it',
                                1
                            );
                        }
                    },
                };
            }}
            columns={columns_quotes}
            loading={props.loading}
            dataSource={props.quotes}
            pagination={false}
        />
    );
};

const OrderResults = (props: { loading: boolean; orders: SalesOrderProps[] }) => {
    const history = useHistory();
    const columns_orders: ColumnProps<SalesOrderProps>[] = [
        {
            title: 'Order Number',
            dataIndex: 'name',
        },
        {
            title: 'PO Number',
            dataIndex: 'purchase_order',
        },
        {
            title: 'Status',
            dataIndex: 'customer_status',
            render: text => (text ? text : ' '),
        },
        {
            title: 'Estimated Ship Date',
            dataIndex: 'required_ship_date',
            render: text => (text ? text.substring(0, 10) : '---'),
        },
        {
            title: 'Total',
            render: (text, record) => <Price value={record.grand_total} />,
        },
    ];

    return (
        <Table<SalesOrderProps>
            bordered
            rowKey={'id'}
            onRow={record => {
                return {
                    onClick: () => history.push('/my-account/orders/' + record.id),
                };
            }}
            columns={columns_orders}
            loading={props.loading}
            dataSource={props.orders}
            pagination={false}
        />
    );
};

const SearchPage = () => {
    const query = useQueryParameters();

    const [criteria, setCriteria] = useState<string>('');
    const [newQuery, setNewQuery] = useState<string>('');
    const c = query.get('query');

    useEffect(() => {
        UIStore.setDocumentTitle('Global Search');
        UIStore.setSelectedKey(['Global Search']);
    }, []);

    useEffect(() => {
        if (c) {
            setNewQuery(c);
        }
    }, [c]);

    useEffect(() => {
        if (criteria) {
            _getRemoteData(criteria);
        }
    }, [criteria]);

    const _getRemoteData = (search: string) => {
        QuoteStore.getQuotes({
            search: search,
            include_quoting: true,
            limit: 10,
        });
        SalesOrderStore.getAllOrders({
            search: search,
            limit: 10,
        });
    };

    return (
        <div className={'search-page'} style={{ minHeight: '90vh' }}>
            <div className={'font-bold text-xl text-center mt-10'}>Search Results</div>
            <div className={'search-input-container flex mt-4'}>
                <Input.Search
                    placeholder="Search"
                    value={newQuery}
                    style={{
                        maxWidth: 400,
                        margin: 'auto',
                    }}
                    onChange={e => setNewQuery(e.currentTarget.value)}
                    onPressEnter={() => setCriteria(newQuery)}
                    onSearch={() => setCriteria(newQuery)}
                />
            </div>

            {!QuoteStore.loading &&
                !SalesOrderStore.loading &&
                QuoteStore.quotes.length === 0 &&
                SalesOrderStore.orders.length === 0 && (
                    <div
                        className={'mt-4'}
                        style={{
                            maxWidth: 400,
                            margin: 'auto',
                            marginTop: 20,
                        }}
                    >
                        We could not find a match for "<span className="font-bold">{criteria}</span>". Please try
                        another search
                    </div>
                )}

            {QuoteStore.quotes.length > 0 && (
                <div className="quotes mt-4">
                    <div className="font-bold text-center text-2xl">Quotes</div>
                    <Row>
                        <QuoteResults loading={QuoteStore.loading} quotes={QuoteStore.quotes} />
                    </Row>
                </div>
            )}

            {SalesOrderStore.orders.length > 0 && (
                <div className="orders mt-4">
                    <div className="font-bold text-center text-2xl">Orders</div>

                    <Row>
                        <OrderResults loading={SalesOrderStore.loading} orders={SalesOrderStore.orders} />
                    </Row>
                </div>
            )}
        </div>
    );
};

export default observer(SearchPage);
